<template>
  <div class="sub-users-container">
    <!-- Header Section -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="header-title-section">
          <h1 class="dashboard-title">Sub Users Management</h1>
          <p class="dashboard-subtitle">Manage sub users and their permissions</p>
        </div>
        <div class="header-actions">
          <button @click="openAddModal" class="header-add-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4V20M4 12H20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>Add Sub User</span>
          </button>
        </div>
        <div class="header-stats" v-if="!isLoading && subUsers.length > 0">
          <div class="stat-card">
            <div class="stat-number">{{ totalCount }}</div>
            <div class="stat-label">Total Users</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">{{ getActiveCount() }}</div>
            <div class="stat-label">Active</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">Loading sub users...</p>
    </div>

    <!-- Content Section -->
    <div v-else class="dashboard-content">
      <!-- Sub Users Table -->
      <div v-if="subUsers.length > 0" class="table-container">
        <table class="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile No</th>
              <th>User Type</th>
              <th>Assigned Properties</th>
              <th>Status</th>
              <th>Created Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in subUsers" :key="user.providerAdminId" class="table-row">
              <td class="user-name">
                <div class="name-cell" @click="openProfileModal(user)" title="Click to view profile">
                  <div class="user-avatar">
                    <img v-if="user.profilepic" :src="user.profilepic" :alt="user.providerAdminName" class="avatar-img" />
                    <span v-else>{{ getInitials(user.providerAdminName) }}</span>
                  </div>
                  <span class="name-text">{{ user.providerAdminName }}</span>
                  <svg class="view-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </td>
              <td>{{ user.email }}</td>
              <td>{{ user.mobileno }}</td>
              <td>
                <span class="type-badge" :class="`type-${user.userType.toLowerCase()}`">
                  {{ user.userType }}
                </span>
              </td>
              <td>
                <div class="assigned-properties">
                  <span v-if="getAssignedProperties(user).length > 0" class="property-count">
                    {{ getAssignedProperties(user).length }} {{ user.adminType || 'Properties' }}
                  </span>
                  <span v-else class="no-properties">No assignments</span>
                </div>
              </td>
              <td>
                <span class="status-badge" :class="`status-${user.status.toLowerCase()}`">
                  <span class="status-dot"></span>
                  {{ user.status }}
                </span>
              </td>
              <td>{{ formatDate(user.createdat) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-state-content">
          <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="empty-icon">
            <path d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <h2 class="empty-title">No Sub Users Yet</h2>
          <p class="empty-description">Get started by adding your first sub user</p>
          <button @click="openAddModal" class="empty-action-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4V20M4 12H20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            Add Your First Sub User
          </button>
        </div>
      </div>
    </div>

    <!-- Add Sub User Modal -->
    <div v-if="showAddModal" class="modal-overlay" @click.self="closeAddModal">
      <div class="modal-content modal-form-content" @click.stop>
        <div class="modal-header">
          <div class="modal-header-wrapper">
            <div class="header-content">
              <div class="icon-wrapper">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                </svg>
              </div>
              <div class="header-text">
                <h2>Add Sub User</h2>
                <p>Create a new sub user account</p>
              </div>
            </div>
            <button class="close-btn" @click="closeAddModal" aria-label="Close modal">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="form-container">
          <form @submit.prevent="submitForm">
            <div class="form-grid">
              <!-- Name Field -->
              <div class="input-group">
                <label for="providerAdminName">Full Name <span class="required">*</span></label>
                <div class="input-wrapper">
                  <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                  </svg>
                  <input 
                    type="text" 
                    id="providerAdminName" 
                    v-model="form.providerAdminName" 
                    required
                    placeholder="Enter full name"
                    :disabled="isSubmitting"
                  />
                </div>
              </div>

              <!-- Email Field -->
              <div class="input-group">
                <label for="email">Email <span class="required">*</span></label>
                <div class="input-wrapper">
                  <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                  </svg>
                  <input 
                    type="email" 
                    id="email" 
                    v-model="form.email" 
                    required
                    placeholder="Enter email address"
                    :disabled="isSubmitting"
                  />
                </div>
              </div>

              <!-- Mobile Number Field -->
              <div class="input-group">
                <label for="mobileno">Mobile Number <span class="required">*</span></label>
                <div class="input-wrapper">
                  <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
                  </svg>
                  <input 
                    type="tel" 
                    id="mobileno" 
                    v-model="form.mobileno" 
                    required
                    placeholder="+9779845678532"
                    :disabled="isSubmitting"
                  />
                </div>
              </div>

              <!-- User Type Field -->
              <div class="input-group">
                <label for="userType">User Type <span class="required">*</span></label>
                <div class="input-wrapper">
                  <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
                  </svg>
                  <select 
                    id="userType" 
                    v-model="form.userType" 
                    required
                    :disabled="isSubmitting"
                    class="select-input"
                  >
                    <option value="">Select user type</option>
                    <option value="MASTER">MASTER</option>
                    <option value="SUBUSER">SUBUSER</option>
                    <option value="OPERATOR">OPERATOR</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <button type="button" class="btn-secondary" @click="closeAddModal" :disabled="isSubmitting">
                Cancel
              </button>
              <button type="submit" class="btn-primary" :disabled="isSubmitting">
                <span v-if="!isSubmitting">Create Sub User</span>
                <span v-else class="loading-content">
                  <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" opacity="0.25"/>
                    <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"/>
                  </svg>
                  Creating...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- User Profile Modal -->
    <div v-if="showProfileModal" class="modal-overlay" @click.self="closeProfileModal">
      <div class="modal-content modal-profile-content" @click.stop>
        <div class="modal-header">
          <div class="modal-header-wrapper">
            <div class="header-content">
              <div class="icon-wrapper">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                </svg>
              </div>
              <div class="header-text">
                <h2>User Profile</h2>
                <p>View user details and information</p>
              </div>
            </div>
            <button class="close-btn" @click="closeProfileModal" aria-label="Close modal">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="profile-container" v-if="selectedUser">
          <div class="profile-header">
            <div class="profile-image-wrapper">
              <img 
                v-if="selectedUser.profilepic" 
                :src="selectedUser.profilepic" 
                :alt="selectedUser.providerAdminName" 
                class="profile-image"
              />
              <div v-else class="profile-image-placeholder">
                {{ getInitials(selectedUser.providerAdminName) }}
              </div>
            </div>
            <h3 class="profile-name">{{ selectedUser.providerAdminName }}</h3>
            <div class="profile-badges">
              <span class="type-badge" :class="`type-${selectedUser.userType.toLowerCase()}`">
                {{ selectedUser.userType }}
              </span>
              <span class="status-badge" :class="`status-${selectedUser.status.toLowerCase()}`">
                <span class="status-dot"></span>
                {{ selectedUser.status }}
              </span>
            </div>
          </div>

          <div class="profile-details">
            <div class="detail-row">
              <div class="detail-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                </svg>
                Email
              </div>
              <div class="detail-value">{{ selectedUser.email }}</div>
            </div>

            <div class="detail-row">
              <div class="detail-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
                </svg>
                Mobile Number
              </div>
              <div class="detail-value">{{ selectedUser.mobileno }}</div>
            </div>

            <div class="detail-row">
              <div class="detail-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
                </svg>
                Admin Type
              </div>
              <div class="detail-value">{{ selectedUser.adminType || 'N/A' }}</div>
            </div>

            <!-- Hostel Assignment Section (only for HOSTEL adminType) -->
            <div class="detail-row hostel-assignment-row" v-if="selectedUser.adminType === 'HOSTEL'">
              <div class="detail-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor"/>
                </svg>
                Assigned Hostels ({{ getAssignedHostels().length }})
              </div>
              <div class="detail-value">
                <div class="hostel-management">
                  <!-- Add Hostel Dropdown (hidden for MASTER users) -->
                  <div class="add-hostel-section" v-if="selectedUser.userType !== 'MASTER'">
                    <div class="select-wrapper">
                      <select 
                        v-model="selectedHostelToAdd" 
                        class="hostel-select"
                        :disabled="isUpdatingHostels || isAddingHostel"
                        @change="addHostel"
                      >
                        <option value="">Select a hostel to add</option>
                        <option 
                          v-for="hostel in getAvailableHostels()" 
                          :key="hostel.objectId" 
                          :value="hostel.objectId"
                        >
                          {{ hostel.objectName }}
                        </option>
                      </select>
                      <div v-if="isAddingHostel" class="select-loading">
                        <svg class="spinner-small" width="16" height="16" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" opacity="0.25"/>
                          <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                    <div v-if="isAddingHostel" class="processing-message">
                      <span>Adding hostel...</span>
                    </div>
                  </div>

                  <!-- Assigned Hostels List -->
                  <div class="assigned-hostels-list" v-if="getAssignedHostels().length > 0">
                    <div 
                      v-for="hostelId in getAssignedHostels()" 
                      :key="hostelId" 
                      class="hostel-item"
                      :class="{ 
                        'processing': processingHostelId === hostelId,
                        'read-only': selectedUser.userType === 'MASTER'
                      }"
                    >
                      <div class="hostel-item-content">
                        <span class="hostel-name">{{ getHostelName(hostelId) }}</span>
                        <div v-if="processingHostelId === hostelId" class="hostel-processing">
                          <svg class="spinner-small" width="14" height="14" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" opacity="0.25"/>
                            <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"/>
                          </svg>
                          <span class="processing-text">Processing...</span>
                        </div>
                      </div>
                      <!-- Remove button hidden for MASTER users -->
                      <button 
                        v-if="selectedUser.userType !== 'MASTER'"
                        class="remove-hostel-btn" 
                        @click="removeHostel(hostelId)"
                        :disabled="isUpdatingHostels || processingHostelId === hostelId"
                        title="Remove hostel"
                      >
                        <svg v-if="processingHostelId !== hostelId" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div v-else class="no-hostels-message">
                    No hostels assigned yet
                  </div>
                </div>
              </div>
            </div>

            <!-- Other Properties Display (for non-HOSTEL or when not managing) -->
            <div class="detail-row" v-if="selectedUser.adminType !== 'HOSTEL' && getAssignedProperties(selectedUser).length > 0">
              <div class="detail-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor"/>
                </svg>
                Assigned {{ selectedUser.adminType || 'Properties' }} ({{ getAssignedProperties(selectedUser).length }})
              </div>
              <div class="detail-value">
                <div class="property-list">
                  <span v-for="(property, index) in getAssignedProperties(selectedUser).slice(0, 10)" :key="index" class="property-tag">
                    {{ property }}
                  </span>
                  <span v-if="getAssignedProperties(selectedUser).length > 10" class="property-tag-more">
                    +{{ getAssignedProperties(selectedUser).length - 10 }} more
                  </span>
                </div>
              </div>
            </div>

            <div class="detail-row">
              <div class="detail-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H19V1h-2v1H7V1H5v1H3.5C2.67 2 2 2.67 2 3.5v17C2 21.33 2.67 22 3.5 22h17c.83 0 1.5-.67 1.5-1.5v-17C22 2.67 21.33 2 20.5 2zm0 18h-17v-14h17v14z" fill="currentColor"/>
                </svg>
                Created Date
              </div>
              <div class="detail-value">{{ formatDate(selectedUser.createdat) }}</div>
            </div>

            <div class="detail-row">
              <div class="detail-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H19V1h-2v1H7V1H5v1H3.5C2.67 2 2 2.67 2 3.5v17C2 21.33 2.67 22 3.5 22h17c.83 0 1.5-.67 1.5-1.5v-17C22 2.67 21.33 2 20.5 2zm0 18h-17v-14h17v14z" fill="currentColor"/>
                </svg>
                Last Updated
              </div>
              <div class="detail-value">{{ formatDate(selectedUser.updatedon) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <transition-group name="toast" tag="div">
        <div 
          v-for="toast in toasts" 
          :key="toast.id" 
          class="toast"
          :class="[`toast-${toast.type}`]"
        >
          <div class="toast-icon">
            <svg v-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <svg v-else-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
            <svg v-else-if="toast.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <svg v-else-if="toast.type === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-message">{{ toast.message }}</p>
          </div>
          <button class="toast-close" @click="removeToast(toast.id)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import { readSessionToken } from '@/utilities/sessionTokenStorage';

export default {
  name: 'SubUsersView',
  data() {
    return {
      subUsers: [],
      totalCount: 0,
      isLoading: true,
      showAddModal: false,
      showProfileModal: false,
      selectedUser: null,
      isSubmitting: false,
      isUpdatingHostels: false,
      isAddingHostel: false,
      processingHostelId: null,
      selectedHostelToAdd: '',
      availableHostels: [],
      toasts: [],
      form: {
        mobileno: '',
        email: '',
        providerAdminName: '',
        userType: ''
      }
    };
  },
  mounted() {
    this.fetchSubUsers();
    this.loadAvailableHostels();
  },
  methods: {
    // Toast notification methods
    showToast(message, type = 'error', duration = 5000) {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, message, type });
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(id);
        }, duration);
      }
    },
    removeToast(id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id);
    },

    // Get authentication token
    getAuthToken() {
      try {
        const token = readSessionToken();
        if (!token) {
          throw new Error('No authentication token found');
        }
        return token;
      } catch (error) {
        console.error('Error getting auth token:', error);
        this.showToast('Authentication error. Please login again.', 'error');
        setTimeout(() => {
          this.$router.push('/login');
        }, 2000);
        throw error;
      }
    },

    // Fetch sub users list
    async fetchSubUsers() {
      this.isLoading = true;
      try {
        const response = await apiService.getSubUsers();

        if (response.data && response.data.data) {
          this.subUsers = response.data.data;
          this.totalCount = response.data.totalcount || response.data.data.length;
        } else {
          this.subUsers = [];
          this.totalCount = 0;
        }
      } catch (error) {
        // Handle "no data" case - set empty array without showing error
        if (isNoDataError(error)) {
          this.subUsers = [];
        } else if (isHmsSubscriptionInactiveError(error)) {
          // Subscription inactive — modal shown in App.vue
        } else {
          console.error('Error fetching sub users:', error);
          this.handleApiError(error, 'Loading sub users');
          this.subUsers = [];
        }
      } finally {
        this.isLoading = false;
      }
    },

    // Open add modal
    openAddModal() {
      this.showAddModal = true;
      document.body.style.overflow = 'hidden';
    },

    // Close add modal
    closeAddModal() {
      this.showAddModal = false;
      this.form = {
        mobileno: '',
        email: '',
        providerAdminName: '',
        userType: ''
      };
      document.body.style.overflow = 'auto';
    },

    // Form validation
    validateForm() {
      if (!this.form.providerAdminName.trim()) {
        this.showToast('Please enter full name.', 'warning');
        return false;
      }
      if (!this.form.email.trim()) {
        this.showToast('Please enter email address.', 'warning');
        return false;
      }
      if (!this.form.mobileno.trim()) {
        this.showToast('Please enter mobile number.', 'warning');
        return false;
      }
      if (!this.form.userType) {
        this.showToast('Please select user type.', 'warning');
        return false;
      }
      return true;
    },

    // Submit form
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.isSubmitting = true;
        
        // Create form data with urlencoded format
        const formData = new URLSearchParams();
        formData.append('mobileno', this.form.mobileno.trim());
        formData.append('email', this.form.email.trim());
        formData.append('providerAdminName', this.form.providerAdminName.trim());
        formData.append('userType', this.form.userType);

        const response = await apiService.createSubUser(formData);

        if (response.data) {
          this.showToast('Sub user created successfully!', 'success', 3000);
          this.closeAddModal();
          // Refresh the list
          this.fetchSubUsers();
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Sub user creation failed:', error);
        this.handleApiError(error, 'Creating sub user');
      } finally {
        this.isSubmitting = false;
      }
    },

    // Enhanced API error handling
    handleApiError(error, context = 'Operation') {
      // Skip error handling for "no data" cases (status 400 with errorcode 2003)
      if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
        return;
      }

      console.error(`${context} error:`, error);
      
      let message = null;
      let type = 'error';

      if (error?.response?.data) {
        const errorData = error.response.data;
        const status = error.response.status;
        
        // For HTTP 400 errors, always use the reason field from API response
        if (status === 400) {
          if (errorData.reason) {
            message = errorData.reason;
          }
          // If no reason provided for 400 error, don't show toast (no generic messages)
        } else {
          // For other status codes, prioritize reason field
          if (errorData.reason) {
            message = errorData.reason;
          } else if (errorData.message) {
            message = errorData.message;
          } else if (errorData.error) {
            message = errorData.error;
          } else {
            // Fallback messages for other status codes only if no reason/message
            switch (status) {
              case 401:
                message = 'Session expired. Please login again.';
                setTimeout(() => {
                  this.$router.push('/login');
                }, 2000);
                break;
              case 403:
                message = 'You do not have permission to perform this action.';
                break;
              case 404:
                message = 'The requested resource was not found.';
                break;
              case 429:
                message = 'Too many requests. Please wait a moment and try again.';
                break;
              case 500:
                message = 'Server error occurred. Please try again later.';
                break;
              case 503:
                message = 'Service temporarily unavailable. Please try again later.';
                break;
            }
          }
        }
      } else if (error?.request) {
        message = 'No response from server. Please check your internet connection.';
      } else if (error?.message) {
        message = error.message;
      }

      // Only show toast if we have a message (genuine errors only)
      if (message) {
        this.showToast(message, type);
      }
    },

    // Get initials for avatar
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    // Format date
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      } catch (error) {
        return dateString;
      }
    },

    // Get active count
    getActiveCount() {
      return this.subUsers.filter(user => user.status === 'ACTIVE').length;
    },

    // Get assigned properties based on adminType
    getAssignedProperties(user) {
      if (!user || !user.adminType) return [];
      
      const adminType = user.adminType.toUpperCase();
      switch (adminType) {
        case 'HOSTEL':
          return user.hostelList && Array.isArray(user.hostelList) ? user.hostelList : [];
        case 'HOTEL':
          return user.hotelList && Array.isArray(user.hotelList) ? user.hotelList : [];
        case 'HOUSE':
          return user.houseList && Array.isArray(user.houseList) ? user.houseList : [];
        case 'PROPERTY':
          return user.propertyList && Array.isArray(user.propertyList) ? user.propertyList : [];
        default:
          return [];
      }
    },

    // Open profile modal
    openProfileModal(user) {
      this.selectedUser = { ...user }; // Create a copy to avoid direct mutation
      this.loadAvailableHostels(); // Reload hostels in case they've been updated
      this.showProfileModal = true;
      document.body.style.overflow = 'hidden';
    },

    // Close profile modal
    closeProfileModal() {
      this.showProfileModal = false;
      this.selectedUser = null;
      this.selectedHostelToAdd = '';
      this.isAddingHostel = false;
      this.processingHostelId = null;
      document.body.style.overflow = 'auto';
    },

    // Load available hostels from localStorage
    loadAvailableHostels() {
      try {
        const cachedData = localStorage.getItem('ADMIN_OBJECT_LIST');
        if (cachedData) {
          const { data } = JSON.parse(cachedData);
          // Filter only HOSTEL type objects
          this.availableHostels = (data || []).filter(obj => obj.objectType === 'HOSTEL');
        }
      } catch (error) {
        console.error('Error loading hostels from localStorage:', error);
        this.availableHostels = [];
      }
    },

    // Get available hostels (not already assigned)
    getAvailableHostels() {
      if (!this.selectedUser) return [];
      const assignedHostels = this.getAssignedHostels();
      return this.availableHostels.filter(hostel => !assignedHostels.includes(hostel.objectId));
    },

    // Get assigned hostels for current user
    getAssignedHostels() {
      if (!this.selectedUser || !this.selectedUser.hostelList) return [];
      return Array.isArray(this.selectedUser.hostelList) ? this.selectedUser.hostelList : [];
    },

    // Get hostel name by ID
    getHostelName(hostelId) {
      const hostel = this.availableHostels.find(h => h.objectId === hostelId);
      return hostel ? hostel.objectName : hostelId;
    },

    // Add hostel to user
    async addHostel() {
      if (!this.selectedHostelToAdd) return;

      const hostelId = this.selectedHostelToAdd;
      const currentHostels = this.getAssignedHostels();

      // Check if already assigned
      if (currentHostels.includes(hostelId)) {
        this.showToast('This hostel is already assigned to the user', 'warning');
        this.selectedHostelToAdd = '';
        return;
      }

      this.isAddingHostel = true;
      this.processingHostelId = hostelId;

      try {
        // Add to array
        const updatedHostels = [...currentHostels, hostelId];
        await this.updateHostelList(updatedHostels);
        this.selectedHostelToAdd = '';
      } finally {
        this.isAddingHostel = false;
        this.processingHostelId = null;
      }
    },

    // Remove hostel from user
    async removeHostel(hostelId) {
      if (this.processingHostelId === hostelId) return;

      this.processingHostelId = hostelId;

      try {
        const currentHostels = this.getAssignedHostels();
        const updatedHostels = currentHostels.filter(id => id !== hostelId);
        await this.updateHostelList(updatedHostels);
      } finally {
        // Small delay to show the processing state
        setTimeout(() => {
          this.processingHostelId = null;
        }, 300);
      }
    },

    // Update hostel list via API
    async updateHostelList(hostelList) {
      if (!this.selectedUser) return;

      try {
        this.isUpdatingHostels = true;

        const response = await apiService.updateSubUser(
          this.selectedUser.providerAdminId,
          { hostelList: hostelList }
        );

        if (response.data) {
          // Update the selected user's hostel list
          this.selectedUser.hostelList = hostelList;
          
          // Update in subUsers array as well
          const userIndex = this.subUsers.findIndex(u => u.providerAdminId === this.selectedUser.providerAdminId);
          if (userIndex !== -1) {
            this.subUsers[userIndex].hostelList = hostelList;
          }

          this.showToast('Hostel assignment updated successfully!', 'success', 3000);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Hostel update failed:', error);
        this.handleApiError(error, 'Updating hostel assignment');
      } finally {
        this.isUpdatingHostels = false;
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = 'auto';
  },
  watch: {
    showProfileModal(newVal) {
      if (!newVal) {
        document.body.style.overflow = 'auto';
      }
    },
    showAddModal(newVal) {
      if (!newVal) {
        document.body.style.overflow = 'auto';
      }
    }
  }
};
</script>

<style scoped>
/* Container */
.sub-users-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding: 0;
}

/* Dashboard Header */
.dashboard-header {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  padding: 2rem 2rem 3rem;
  margin-bottom: 2rem;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 8px 32px rgba(91, 46, 142, 0.15);
  position: sticky;
  top: 80px;
  z-index: 100;
  overflow: hidden;
}

.dashboard-header::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(50px, -50px);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
}

.header-title-section {
  flex: 1;
}

.dashboard-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.dashboard-subtitle {
  font-size: 1.125rem;
  color: #cbd5e1;
  margin: 0;
  font-weight: 400;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-add-btn {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  outline: none;
  white-space: nowrap;
}

.header-add-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.header-add-btn svg {
  width: 16px;
  height: 16px;
}

.header-stats {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 2;
  width: 100%;
  margin-top: 1rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  min-width: 120px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
  line-height: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #cbd5e1;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Dashboard Content */
.dashboard-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 2rem;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 2rem;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #5b2e8e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  color: #64748b;
  font-size: 1rem;
  font-weight: 500;
}

/* Table Container */
.table-container {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  overflow-x: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table thead {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.users-table th {
  padding: 1.25rem 1.5rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
  vertical-align: middle;
}

.users-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.users-table tbody tr:hover {
  background: #f8fafc;
}

.users-table td {
  padding: 1.25rem 1.5rem;
  font-size: 0.9375rem;
  color: #1e293b;
  white-space: nowrap;
  vertical-align: middle;
  text-align: left;
}

.user-name {
  font-weight: 600;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 4px 8px;
  margin: -4px -8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
}

.name-cell:hover {
  background: #f8fafc;
  transform: translateX(2px);
}

.name-cell:hover .name-text {
  color: #5b2e8e;
  text-decoration: underline;
}

.name-text {
  transition: all 0.2s ease;
  color: #1e293b;
}

.view-icon {
  opacity: 0;
  color: #5b2e8e;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: auto;
}

.name-cell:hover .view-icon {
  opacity: 1;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
  overflow: hidden;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.assigned-properties {
  display: flex;
  align-items: center;
}

.property-count {
  font-size: 0.875rem;
  color: #5b2e8e;
  font-weight: 600;
  padding: 4px 12px;
  background: #ede9fe;
  border-radius: 12px;
  border: 1px solid #c4b5fd;
}

.no-properties {
  font-size: 0.875rem;
  color: #9ca3af;
  font-style: italic;
}

.type-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.type-master {
  background: #eff6ff;
  color: #1e40af;
}

.type-subuser {
  background: #f0fdf4;
  color: #166534;
}

.type-operator {
  background: #fef3c7;
  color: #92400e;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-active {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #d1fae5;
}

.status-active .status-dot {
  background: #10b981;
}

.status-pending {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.status-pending .status-dot {
  background: #f59e0b;
}

.status-inactive {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.status-inactive .status-dot {
  background: #ef4444;
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 2rem;
}

.empty-state-content {
  text-align: center;
  max-width: 400px;
}

.empty-icon {
  color: #cbd5e1;
  margin-bottom: 2rem;
  opacity: 0.6;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.empty-description {
  font-size: 1rem;
  color: #64748b;
  margin: 0 0 2rem 0;
  line-height: 1.5;
}

.empty-action-btn {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 0.875rem 1.75rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.empty-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(91, 46, 142, 0.4);
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out;
  overflow-y: auto;
  padding: 20px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Modal Content */
.modal-content {
  background: #ffffff;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease-out;
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr;
  margin: auto;
  margin-top: 5vh;
  margin-bottom: 5vh;
}

.modal-form-content {
  max-width: 600px;
}

.modal-profile-content {
  max-width: 700px;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Modal Header */
.modal-header {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  padding: 24px 32px;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
  pointer-events: none;
}

.modal-header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
  gap: 16px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
  min-width: 0;
}

.header-text {
  min-width: 0;
  margin: 0;
  padding: 0;
  flex: 1;
}

.icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
}

.modal-header h2 {
  margin: 0 0 4px 0;
  padding: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  word-wrap: break-word;
  text-align: left;
}

.modal-header p {
  margin: 0;
  padding: 0;
  font-size: 14px;
  opacity: 0.9;
  font-weight: 400;
  text-align: left;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  flex-shrink: 0;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* Form Container */
.form-container {
  padding: 32px;
  overflow-y: auto;
  max-height: calc(90vh - 120px);
  margin-top: 0;
}

/* Profile Container */
.profile-container {
  padding: 32px;
  overflow-y: auto;
  max-height: calc(90vh - 120px);
}

.profile-header {
  text-align: center;
  padding-bottom: 32px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 32px;
}

.profile-image-wrapper {
  margin-bottom: 16px;
}

.profile-image,
.profile-image-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  margin: 0 auto;
  object-fit: cover;
  border: 4px solid #e5e7eb;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 700;
}

.profile-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
}

.profile-badges {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.profile-details {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.detail-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  flex-shrink: 0;
}

.detail-label svg {
  color: #9ca3af;
  flex-shrink: 0;
}

.detail-value {
  font-size: 1rem;
  color: #1e293b;
  font-weight: 500;
  word-break: break-word;
  text-align: left;
  align-self: flex-start;
}

.property-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.property-tag,
.property-tag-more {
  display: inline-block;
  padding: 6px 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #475569;
  font-weight: 500;
}

.property-tag-more {
  background: #ede9fe;
  border-color: #c4b5fd;
  color: #5b21b6;
}

/* Hostel Management Styles */
.hostel-assignment-row {
  border-top: 2px solid #e5e7eb;
  padding-top: 24px;
  margin-top: 8px;
}

.hostel-management {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.add-hostel-section {
  margin-bottom: 8px;
}

.select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.select-loading {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  pointer-events: none;
  color: #5b2e8e;
}

.spinner-small {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.processing-message {
  margin-top: 8px;
  padding: 8px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1e40af;
  display: flex;
  align-items: center;
  gap: 8px;
}

.processing-message::before {
  content: '';
  width: 12px;
  height: 12px;
  border: 2px solid #3b82f6;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.hostel-select {
  width: 100%;
  padding: 10px 16px;
  padding-right: 40px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9375rem;
  background: #ffffff;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.hostel-select:disabled {
  padding-right: 50px;
  opacity: 0.7;
}

.hostel-select:focus {
  outline: none;
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.hostel-select:disabled {
  background: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
  opacity: 0.6;
}

.assigned-hostels-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.hostel-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.hostel-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(91, 46, 142, 0.1), transparent);
  transition: left 0.5s;
}

.hostel-item.processing::before {
  left: 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

.hostel-item:hover:not(.processing) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.hostel-item.processing {
  opacity: 0.7;
  background: #f0f9ff;
  border-color: #bae6fd;
  pointer-events: none;
}

.hostel-item.read-only {
  background: #f9fafb;
  border-color: #e5e7eb;
  cursor: default;
}

.hostel-item.read-only:hover {
  background: #f9fafb;
  border-color: #e5e7eb;
}

.hostel-item-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.hostel-processing {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3b82f6;
  font-size: 0.8125rem;
}

.processing-text {
  color: #3b82f6;
  font-weight: 500;
}

.hostel-name {
  font-size: 0.9375rem;
  color: #1e293b;
  font-weight: 500;
  flex: 1;
}

.remove-hostel-btn {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remove-hostel-btn:hover:not(:disabled) {
  background: #fee2e2;
  border-color: #fca5a5;
  transform: scale(1.05);
}

.remove-hostel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.remove-hostel-btn svg {
  width: 16px;
  height: 16px;
}

.no-hostels-message {
  padding: 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  font-style: italic;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px dashed #e5e7eb;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Labels */
label {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  line-height: 1.4;
}

.required {
  color: #ef4444;
  font-size: 12px;
  margin-left: 2px;
}

/* Input Wrapper */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #6b7280;
  z-index: 1;
  pointer-events: none;
}

/* Input Styles */
input,
select {
  width: 100%;
  padding: 14px 16px 14px 44px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  background: #ffffff;
  transition: all 0.2s ease;
  box-sizing: border-box;
  font-family: inherit;
}

input:focus,
select:focus {
  outline: none;
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

input:disabled,
select:disabled {
  background: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}

input::placeholder {
  color: #9ca3af;
  font-size: 14px;
}

.select-input {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 40px;
  cursor: pointer;
}

/* Button Styles */
.btn-primary {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 25px -5px rgba(91, 46, 142, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-secondary {
  background: #ffffff;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
}

.btn-secondary:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
}

/* Loading Animation */
.loading-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  animation: spin 1s linear infinite;
}

/* Toast Notification Styles */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-left: 4px solid transparent;
  min-width: 320px;
  pointer-events: auto;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.toast-error {
  border-left-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2, #ffffff);
}

.toast-success {
  border-left-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4, #ffffff);
}

.toast-info {
  border-left-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff, #ffffff);
}

.toast-warning {
  border-left-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb, #ffffff);
}

.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.toast-error .toast-icon {
  color: #ef4444;
}

.toast-success .toast-icon {
  color: #10b981;
}

.toast-info .toast-icon {
  color: #3b82f6;
}

.toast-warning .toast-icon {
  color: #f59e0b;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-message {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-close {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toast-close:hover {
  color: #6b7280;
  background: rgba(0, 0, 0, 0.05);
  transform: scale(1.1);
}

/* Toast Animations */
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .header-content {
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
  }
  
  .header-actions {
    justify-content: center;
  }
  
  .header-stats {
    justify-content: center;
    order: 3;
    margin-top: 1.5rem;
  }
  
  .header-title-section {
    order: 1;
    text-align: center;
  }
  
  .header-actions {
    order: 2;
  }
  
  .dashboard-title {
    font-size: 2rem;
  }
}

@media (max-width: 1024px) {
  .table-container {
    overflow-x: auto;
  }

  .users-table {
    min-width: 800px;
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    padding: 1.5rem 1rem 2rem;
    top: 60px;
  }
  
  .dashboard-content {
    padding: 0 1rem 2rem;
  }
  
  .dashboard-title {
    font-size: 1.75rem;
  }
  
  .dashboard-subtitle {
    font-size: 1rem;
  }
  
  .header-add-btn {
    padding: 0.625rem 1.25rem;
    font-size: 0.8125rem;
  }
  
  .header-stats {
    gap: 1rem;
    justify-content: center;
  }
  
  .stat-card {
    min-width: 100px;
    padding: 1rem;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }

  .table-container {
    border-radius: 12px;
  }

  .users-table th,
  .users-table td {
    padding: 1rem;
    font-size: 0.875rem;
  }

  .modal-overlay {
    padding: 16px;
    align-items: flex-start;
    padding-top: 60px;
  }
  
  .modal-content {
    margin: 0;
    width: 100%;
    max-height: calc(100vh - 92px);
    margin-top: 0;
  }

  .modal-header-wrapper {
    gap: 12px;
  }

  .header-content {
    gap: 0;
  }
  
  .icon-wrapper {
    margin-right: 12px;
  }

  .modal-header h2 {
    font-size: 20px;
  }

  .close-btn {
    width: 36px;
    height: 36px;
  }

  .modal-header {
    padding: 20px 24px;
  }

  .form-container {
    padding: 24px;
  }

  .detail-row {
    grid-template-columns: 150px 1fr;
    gap: 16px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }

  .toast-container {
    top: 16px;
    right: 16px;
    left: 16px;
    max-width: none;
  }

  .toast {
    min-width: auto;
    width: 100%;
    padding: 14px 16px;
  }

  .toast-message {
    font-size: 14px;
  }
}

@media (max-width: 640px) {
  .dashboard-header {
    padding: 1rem 0.75rem 1.5rem;
  }
  
  .dashboard-content {
    padding: 0 0.75rem 1.5rem;
  }
  
  .dashboard-title {
    font-size: 1.5rem;
  }
  
  .header-add-btn {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
  }
  
  .header-stats {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: 0.75rem;
  }
  
  .stat-card {
    min-width: 80px;
    padding: 0.75rem;
  }
  
  .stat-number {
    font-size: 1.25rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .users-table th,
  .users-table td {
    padding: 0.75rem;
    font-size: 0.8125rem;
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    font-size: 0.75rem;
  }

  .empty-icon {
    width: 80px;
    height: 80px;
  }
  
  .empty-title {
    font-size: 1.25rem;
  }

  .modal-overlay {
    padding: 12px;
    padding-top: 50px;
  }
  
  .modal-content {
    max-height: calc(100vh - 82px);
    margin-top: 0;
  }
  
  .modal-header {
    padding: 16px 20px;
  }

  .modal-header-wrapper {
    gap: 8px;
  }

  .header-content {
    gap: 0;
  }
  
  .icon-wrapper {
    margin-right: 8px;
  }

  .modal-header h2 {
    font-size: 18px;
  }

  .modal-header p {
    font-size: 12px;
  }

  .close-btn {
    width: 32px;
    height: 32px;
  }

  .icon-wrapper {
    padding: 8px;
  }

  .form-container,
  .profile-container {
    padding: 20px;
  }

  .profile-image,
  .profile-image-placeholder {
    width: 100px;
    height: 100px;
    font-size: 2rem;
  }

  .detail-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .hostel-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .remove-hostel-btn {
    align-self: flex-end;
  }
}

@media (max-width: 480px) {
  .table-container {
    border-radius: 8px;
  }

  .users-table {
    min-width: 700px;
  }

  .empty-illustration {
    max-width: 240px;
  }
  
  .modal-content {
    width: 98vw;
    max-width: none;
    max-height: 98vh;
    border-radius: 12px;
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus states for accessibility */
.btn-primary:focus,
.btn-secondary:focus,
.close-btn:focus,
.header-add-btn:focus,
.empty-action-btn:focus {
  outline: 2px solid #5b2e8e;
  outline-offset: 2px;
}

input:focus,
select:focus {
  outline: none;
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

* {
  box-sizing: border-box;
}
</style>
